import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { AdminActionsLanguage } from '../../../../../i18n'
import { AcdStatus, ActionField, AdminAction, ForwardStatus, MonitorType } from './Types'
import { TelSysTypes } from '../../../../NonReact/constants/TelephonyServerTypes'

export enum ActionIDs {
    Device = 'device',
    Destination = 'destination',
    CallID = 'callID',
    DestinationAutoPrefix = 'destinationAutoPrefix',
    CallIDHeld = 'callIDHeld',
    ParkLocation = 'parkLocation',
    DeviceToPickupAt = 'deviceToPickupAt',
    MonitorType = 'monitorType',
    Password = 'password',
    UseSecurePassword = 'useSecurePassword',
    MonitorCrossRefID = 'monitorCrossRefID',
    IsDND = 'DNDStatus',
    AgentID = 'ACDAgentId',
    ACDGroup = 'ACDGroup',
    ACDStateReasonTextID = 'ACDStateReasonTextID',
    ACDStatus = 'ACDStatus',
    ForwardStatus = 'forwardStatus',
    AccountCode = 'accountCode',
    PasswordOld = 'passwordOld',
    PasswordNew = 'passwordNew',
    DTMFTones = 'DTMFTones',
    OtherCallID = 'otherCallId',
    CallParked = 'callParked',
    Policies = 'policies'
}

export const getActions = (
    language: CLanguageHandler,
    telsysType: TelSysTypes
): Partial<Record<AdminAction, ActionField[]>> => {
    const deviceField = (required?: boolean): ActionField => ({
        id: ActionIDs.Device,
        label: language.get(AdminActionsLanguage.Field_Label_DeviceMain),
        type: 'device',
        required
    })

    const destinationField: ActionField = {
        id: ActionIDs.Destination,
        label: language.get(AdminActionsLanguage.Field_Label_Destination),
        type: 'string'
    }

    const activeCallIdField: ActionField = {
        id: ActionIDs.CallID,
        label: language.get(AdminActionsLanguage.Field_Label_CallIDMain),
        type: 'string'
    }

    const destinationAutoPrefixField: ActionField = {
        id: ActionIDs.DestinationAutoPrefix,
        label: language.get(AdminActionsLanguage.Field_Label_DestinationAutoPrefix),
        type: 'boolean'
    }

    const otherCallIDField: ActionField = {
        id: ActionIDs.OtherCallID,
        label: language.get(AdminActionsLanguage.Field_Label_CallIDHeld),
        type: 'string'
    }

    const policiesField: ActionField = {
        id: ActionIDs.Policies,
        label: language.get(AdminActionsLanguage.Field_Label_Policies),
        type: 'textarea',
        required: true
    }

    if (telsysType === TelSysTypes.MS_TEAMS) {
        return {
            [AdminAction.GetTenant]: [],
            [AdminAction.DeleteTenant]: [],
            [AdminAction.GetUsers]: [],
            [AdminAction.GetVoiceApps]: [],
            [AdminAction.GetUserCompliancePolicy]: [deviceField()],
            [AdminAction.ApplyAkixiCapturePolicy]: [deviceField(true), policiesField],
            [AdminAction.QueryPresenceStatus]: [deviceField(true)],
            [AdminAction.DeleteUserPolicy]: [deviceField(true), policiesField],
            [AdminAction.ClearCallViaCallControl]: [activeCallIdField],
            [AdminAction.ClearCallViaCallModel]: [activeCallIdField],
            [AdminAction.ClearConnection]: [deviceField(true), activeCallIdField]
        }
    }

    return {
        [AdminAction.MakeCall]: [deviceField(true), destinationField, destinationAutoPrefixField],
        [AdminAction.ConsultationCall]: [
            deviceField(true),
            destinationField,
            activeCallIdField,
            destinationAutoPrefixField
        ],
        [AdminAction.DialDTMFTone]: [
            deviceField(),
            activeCallIdField,
            {
                id: ActionIDs.DTMFTones,
                label: language.get(AdminActionsLanguage.Field_Label_DTMFTones),
                type: 'string'
            }
        ],
        [AdminAction.ClearCallViaCallControl]: [activeCallIdField],
        [AdminAction.ClearCallViaCallModel]: [activeCallIdField],
        [AdminAction.ClearConnection]: [deviceField(true), activeCallIdField],
        [AdminAction.AnswerCall]: [deviceField(true), activeCallIdField],
        [AdminAction.HoldCall]: [deviceField(true), activeCallIdField],
        [AdminAction.ParkCall]: [
            deviceField(true),
            activeCallIdField,
            {
                id: ActionIDs.ParkLocation,
                label: language.get(AdminActionsLanguage.Field_Label_ParkLocation),
                type: 'string'
            }
        ],
        [AdminAction.RetrieveCall]: [deviceField(true), activeCallIdField],
        [AdminAction.AlternateCall]: [deviceField(true), activeCallIdField, otherCallIDField],
        [AdminAction.PickupCall]: [
            deviceField(true),
            activeCallIdField,
            {
                id: ActionIDs.DeviceToPickupAt,
                label: language.get(AdminActionsLanguage.Field_Label_DeviceToPickupAt),
                type: 'string'
            },
            {
                id: ActionIDs.CallParked,
                label: language.get(AdminActionsLanguage.Field_Label_CallParked),
                type: 'boolean'
            }
        ],
        [AdminAction.TransferOneStep]: [
            deviceField(true),
            destinationField,
            activeCallIdField,
            destinationAutoPrefixField
        ],
        [AdminAction.TransferComplete]: [deviceField(true), activeCallIdField, otherCallIDField],
        [AdminAction.DivertDeflectCall]: [
            deviceField(true),
            destinationField,
            activeCallIdField,
            destinationAutoPrefixField
        ],
        [AdminAction.MonitorConnection]: [
            deviceField(true),
            destinationField,
            activeCallIdField,
            {
                id: ActionIDs.MonitorType,
                label: language.get(AdminActionsLanguage.Field_Label_MonitorType),
                type: 'list',
                options: [
                    {
                        value: MonitorType.SilentMonitor,
                        label: language.get(AdminActionsLanguage.Field_Label_SilentMonitor)
                    },
                    {
                        value: MonitorType.ForcedIntrude,
                        label: language.get(AdminActionsLanguage.Field_Label_ForcedIntrude)
                    }
                ]
            }
        ],
        [AdminAction.ConferenceJoin]: [deviceField(true), activeCallIdField, otherCallIDField],
        [AdminAction.MonitorStart]: [
            deviceField(true),
            {
                id: ActionIDs.Password,
                label: language.get(AdminActionsLanguage.Field_Label_Password),
                type: 'password'
            },
            {
                id: ActionIDs.UseSecurePassword,
                label: language.get(AdminActionsLanguage.Field_Label_UseSecurePassword),
                type: 'boolean'
            }
        ],
        [AdminAction.MonitorStop]: [
            deviceField(true),
            {
                id: ActionIDs.MonitorCrossRefID,
                label: language.get(AdminActionsLanguage.Field_Label_MonitorCrossRefID),
                type: 'string'
            }
        ],
        [AdminAction.SetDNDStatus]: [
            deviceField(true),
            {
                id: ActionIDs.IsDND,
                label: language.get(AdminActionsLanguage.Field_Label_IsDND),
                type: 'boolean'
            }
        ],
        [AdminAction.SetACDStat]: [
            deviceField(true),
            {
                id: ActionIDs.AgentID,
                label: language.get(AdminActionsLanguage.Field_Label_AgentID),
                type: 'string'
            },
            {
                id: ActionIDs.ACDGroup,
                label: language.get(AdminActionsLanguage.Field_Label_ACDGroup),
                type: 'string'
            },
            {
                id: ActionIDs.Password,
                label: language.get(AdminActionsLanguage.Field_Label_Password),
                type: 'password'
            },
            {
                id: ActionIDs.ACDStateReasonTextID,
                label: language.get(AdminActionsLanguage.Field_Label_ACDStateReasonTextID),
                type: 'string' // value is always 1
            },
            {
                id: ActionIDs.ACDStatus,
                label: language.get(AdminActionsLanguage.Field_Label_ACDStatus),
                type: 'list',
                options: [
                    { label: language.get(AdminActionsLanguage.AcdOption_SignIn), value: AcdStatus.SignIn },
                    { label: language.get(AdminActionsLanguage.AcdOption_SignOut), value: AcdStatus.SignOut },
                    { label: language.get(AdminActionsLanguage.AcdOption_Available), value: AcdStatus.Available },
                    {
                        label: language.get(AdminActionsLanguage.AcdOption_NotAvailable),
                        value: AcdStatus.NotAvailable
                    },
                    {
                        label: language.get(AdminActionsLanguage.AcdOption_AlertingAcd),
                        value: AcdStatus.AlertingAcd
                    },
                    {
                        label: language.get(AdminActionsLanguage.AcdOption_AlertingNonAcd),
                        value: AcdStatus.AlertingNonAcd
                    },
                    { label: language.get(AdminActionsLanguage.AcdOption_BusyAcd), value: AcdStatus.BusyAcd },
                    {
                        label: language.get(AdminActionsLanguage.AcdOption_BusyNonAcd),
                        value: AcdStatus.BusyNonAcd
                    },
                    {
                        label: language.get(AdminActionsLanguage.AcdOption_BusySupervisor),
                        value: AcdStatus.BusySupervisor
                    },
                    { label: language.get(AdminActionsLanguage.AcdOption_AcdDnd), value: AcdStatus.AcdDnd },
                    { label: language.get(AdminActionsLanguage.AcdOption_WrapUp), value: AcdStatus.WrapUp }
                ]
            }
        ],
        [AdminAction.SetForwardStatus]: [
            deviceField(true),
            destinationField,
            {
                id: ActionIDs.ForwardStatus,
                label: language.get(AdminActionsLanguage.Field_Label_ForwardMode),
                type: 'list',
                options: [
                    { label: language.get(AdminActionsLanguage.ForwardOption_None), value: ForwardStatus.None },
                    {
                        label: language.get(AdminActionsLanguage.ForwardOption_Immediate),
                        value: ForwardStatus.Immediate
                    },
                    {
                        label: language.get(AdminActionsLanguage.ForwardOption_OnBusy),
                        value: ForwardStatus.OnBusy
                    },
                    {
                        label: language.get(AdminActionsLanguage.ForwardOption_NoAnswer),
                        value: ForwardStatus.NoAnswer
                    },
                    { label: language.get(AdminActionsLanguage.ForwardOption_OnDnd), value: ForwardStatus.OnDND },
                    { label: language.get(AdminActionsLanguage.ForwardOption_OnOos), value: ForwardStatus.OnOOS }
                ]
            }
        ],
        [AdminAction.SetAccountCode]: [
            deviceField(true),
            activeCallIdField,
            {
                id: ActionIDs.AccountCode,
                label: language.get(AdminActionsLanguage.Field_Label_AccountCode),
                type: 'string'
            }
        ],
        [AdminAction.QueryDNDStat]: [deviceField(true)],
        [AdminAction.QueryACDStat]: [deviceField(true)],
        [AdminAction.QueryForwardStatus]: [deviceField(true)],
        [AdminAction.SnapshotCall]: [deviceField(true), activeCallIdField],
        [AdminAction.SnapshotDevice]: [deviceField(true)]
    }
}
